import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  TableContainer,
  StyledTable,
  StyledTHead,
} from './ReusableTable.styles';
import { extractNumericValue, formatTotalValue } from '../../utils/helpers';

export type ReusableTableProps<T = Record<string, string | number | null>> = {
  optionalSuperTHeaders?: string[];
  tableHeaders: string[];
  totalColumns?: (keyof T)[];
  totalValues?: Record<string, number>;
  rowsData: T[];
  renderRow: (item: T, index: number) => React.ReactNode;
  showTotalRow?: boolean;
};

export const ReusableTable = <T,>({
  optionalSuperTHeaders,
  tableHeaders,
  totalColumns,
  totalValues,
  rowsData,
  renderRow,
  showTotalRow = true,
}: ReusableTableProps<T>) => {
  const { t } = useTranslation();

  // Calculate totals for specified columns
  const calculateTotals = () => {
    if (!rowsData || rowsData.length === 0 || !totalColumns) return {};

    return totalColumns?.reduce((totals, columnKey) => {
      totals[columnKey as string] = rowsData.reduce((sum, item) => {
        return sum + extractNumericValue(item[columnKey]);
      }, 0);
      return totals;
    }, {} as Record<string, number>);
  };

  const renderCalculatedTotalRow = () => {
    if (!rowsData || rowsData.length === 0) return null;

    const totals = calculateTotals();
    const keys = Object.keys(
      rowsData[0] as Record<string, string | number | null>
    );

    return (
      <tr>
        {tableHeaders.map((header, index) => {
          if (index === 0) {
            return (
              <td key={`total-cell-${index}`}>
                <strong>{t('table-total-label')}</strong>
              </td>
            );
          }

          const columnKey = keys[index] as keyof T;
          if (totalColumns?.includes(columnKey)) {
            // the total value to be shown is either taken from BE response or calculated by helper function if BE response doesn't include it
            const backendTotal = totalValues?.[columnKey as string];
            const displayValue =
              backendTotal !== undefined
                ? backendTotal
                : totals[columnKey as string];
            return (
              <td key={`total-cell-${index}`}>
                <strong>
                  {formatTotalValue(displayValue, rowsData[0][columnKey])}
                </strong>
              </td>
            );
          }

          const shouldHideDash = (columnKey as string).includes(
            'percentageConsumed'
          );
          return (
            <td key={`total-cell-${index}`}>{shouldHideDash ? '' : '-'}</td>
          );
        })}
      </tr>
    );
  };

  return (
    <TableContainer>
      <StyledTable>
        <StyledTHead>
          <tr>
            {optionalSuperTHeaders?.map((header, index) => {
              const numColumns = index === 0 ? 1 : (tableHeaders.length - 1) / (optionalSuperTHeaders.length - 1);
              return (
                <th key={`super-header-${index}`} colSpan={numColumns}>
                  {header}
                </th>
              );
            })}
          </tr>
          <tr>
            {tableHeaders.map((header, index) => (
              <th key={`header-${index}`}>{header}</th>
            ))}
          </tr>
        </StyledTHead>
        {/* TODO: This condition needs to be checked again as confirmation is needed whether if no data is present then would the tableHeaders be still sent or not */}
        <tbody>
        {!rowsData || rowsData.length === 0 ? (
          <tr>
            <td colSpan={tableHeaders.length}>{t('table-no-data-label')}</td>
          </tr>
        ) : (
          <>
            {rowsData.map((item, index) => (
              <tr key={`row-${index}`}>{renderRow(item, index)}</tr>
            ))}
            {showTotalRow &&
              totalColumns &&
              totalColumns.length > 0 &&
              renderCalculatedTotalRow()}
          </>
        )}
        </tbody>
      </StyledTable>
    </TableContainer>
  );
};
